import { useState } from 'react';
import {
  type UpdateUserAvatarInput,
  useUpdateUserAvatar,
} from 'src/mutations/avatar/updateUserAvatarMutation';
import { useQueryParams } from '@/utils/use-query-params';
import { useIdentity } from '$api-clients/hooks';

/**
 * Shared logic for the account-settings header + team switcher.
 *
 * Sources the current user and their teams from `useIdentity('/auth/userinfo')`
 * — the page is always the signed-in user's own account. `accessibleTeams.nodes`
 * supplies each team's `alias`/`legalName`. Team `status` and `supervisingBroker`
 * are not on `/auth/userinfo`, so `ViewTeamSelector` fetches `/teams/{teamId}`
 * per option for those.
 */
export const useAccountSettingsLogic = () => {
  const { addQueryParam, removeQueryParam } = useQueryParams();

  const { data: user, isLoading } = useIdentity('/auth/userinfo');

  const userId = user?.id ?? '';
  const [updateAvatarMutation, { loading: loadingUpdateAvatar }] =
    useUpdateUserAvatar(userId);
  const [avatarEditModalOpen, setAvatarEditModalOpen] = useState(false);

  const name =
    user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`;

  const accessibleNodes = user?.accessibleTeams?.nodes ?? {};
  const teams = (user?.teams ?? []).flatMap((membership) => {
    const node = accessibleNodes[membership.id];
    return node
      ? { id: membership.id, alias: node.alias, legalName: node.legalName }
      : [];
  });

  const handleTeamChange = (teamId: string) => {
    if (teamId) {
      addQueryParam('team', teamId);
    } else {
      removeQueryParam('team');
    }
  };

  const onSubmitAvatar = (input: UpdateUserAvatarInput) =>
    updateAvatarMutation(input).then((success) => {
      if (success) setAvatarEditModalOpen(false);
    });

  return {
    user,
    isLoading,
    name,
    teams,
    avatarEditModalOpen,
    setAvatarEditModalOpen,
    loadingUpdateAvatar,
    handleTeamChange,
    onSubmitAvatar,
  };
};
